import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Bot, Globe, Megaphone, LineChart, GraduationCap, Scale, Check } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

type ServiceKey = 'ai' | 'web' | 'social' | 'seo' | 'training' | 'dpo';

type ServiceDef = {
  id: ServiceKey;
  key: ServiceKey;
  link: string;
  Icon: React.ComponentType<{ className?: string }>;
  /** Full-width hero treatment with lime accent + shimmer border */
  featured?: boolean;
  /** Full-width row without the featured styling (regular accent colours) */
  wide?: boolean;
};

const services: ServiceDef[] = [
  { id: 'ai',       key: 'ai',       link: '/services/ai',       Icon: Bot,            featured: true },
  { id: 'web',      key: 'web',      link: '/services/web',      Icon: Globe },
  { id: 'social',   key: 'social',   link: '/services/social',   Icon: Megaphone },
  { id: 'seo',      key: 'seo',      link: '/services/seo',      Icon: LineChart },
  { id: 'training', key: 'training', link: '/services/training', Icon: GraduationCap },
  { id: 'dpo',      key: 'dpo',      link: '/services/dpo',      Icon: Scale,          wide: true },
];

const ServicesSection: React.FC = () => {
  const { t } = useLanguage();
  const ref = useRef<HTMLElement | null>(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section
      id="services"
      ref={ref}
      className="relative py-24 md:py-32 bg-background"
      aria-labelledby="services-heading"
    >
      {/* Background detail */}
      <div className="pointer-events-none absolute inset-0 grid-lines opacity-[0.06]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-background to-transparent" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header — asymmetric editorial */}
        <motion.div
          className="grid grid-cols-12 gap-y-6 mb-16 md:mb-20"
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.8 }}
        >
          <div className="col-span-12 md:col-span-7">
            <span className="eyebrow">— {t('services.eyebrow')}</span>
            <h2
              id="services-heading"
              className="mt-4 font-display text-foreground text-[clamp(2rem,4.2vw,3.75rem)] leading-[1.04] tracking-tight"
            >
              {t('services.title')}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              {t('services.subtitle')}
            </p>
          </div>
        </motion.div>

        {/* Service cards — featured AI banner + 2x2 grid of the rest */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {services.map((s, idx) => {
            const { Icon, featured, wide } = s;
            const baseKey = `services.${s.key}`;
            // AI takes full row (featured banner); 4 standard cards sit in a 2x2 grid;
            // DPO closes with another full-width row (wide, regular styling).
            const span = featured || wide ? 'md:col-span-2' : 'md:col-span-1';

            return (
              <motion.article
                key={s.id}
                className={span}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.7, delay: 0.1 + idx * 0.1 }}
              >
                <Link
                  to={s.link}
                  className={[
                    'group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border p-5 sm:p-7 md:p-9 transition-all duration-500',
                    featured
                      ? 'bg-card border-lime/30 shimmer-border hover:border-lime/50'
                      : 'bg-card/60 border-border/80 hover:bg-card hover:border-foreground/30',
                  ].join(' ')}
                  aria-label={t(`${baseKey}.title`)}
                >
                  {/* Decorative corner */}
                  <div
                    className={[
                      'pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl opacity-0 transition-opacity duration-700 group-hover:opacity-100',
                      featured ? 'bg-lime/20' : 'bg-primary/15',
                    ].join(' ')}
                    aria-hidden="true"
                  />

                  {/* Top row: number + tag */}
                  <header className="relative z-10 flex items-start justify-between gap-4">
                    <span
                      className={[
                        'font-mono-tag text-xs uppercase tracking-[0.32em]',
                        featured ? 'text-lime' : 'text-muted-foreground',
                      ].join(' ')}
                    >
                      / {t(`${baseKey}.number`)}
                    </span>
                    {featured && (
                      <span className="pill-tag-lime">
                        <span className="h-1.5 w-1.5 rounded-full bg-lime animate-pulse" />
                        {t(`${baseKey}.tag`)}
                      </span>
                    )}
                  </header>

                  {/* Title + icon */}
                  <div className="relative z-10 mt-8 md:mt-16">
                    <div className="flex items-end justify-between gap-4 mb-4">
                      <h3 className="font-display text-foreground text-3xl md:text-4xl lg:text-[2.65rem] leading-[1.05] tracking-tight">
                        {t(`${baseKey}.title`)}
                      </h3>
                      <Icon
                        className={[
                          'h-9 w-9 flex-shrink-0 transition-transform duration-500 group-hover:rotate-[-6deg] group-hover:scale-110',
                          featured ? 'text-lime' : 'text-primary',
                        ].join(' ')}
                        strokeWidth={1.5}
                      />
                    </div>
                    <p className={[
                      'font-display italic text-lg md:text-xl mb-5',
                      featured ? 'text-lime/90' : 'text-primary-light',
                    ].join(' ')}>
                      {t(`${baseKey}.oneLiner`)}
                    </p>
                    <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-6">
                      {t(`${baseKey}.description`)}
                    </p>

                    {/* Bullets */}
                    <ul className="space-y-2.5 mb-8">
                      {['bullet1', 'bullet2', 'bullet3'].map((b) => (
                        <li key={b} className="flex items-start gap-3 text-sm text-foreground/85">
                          <Check
                            className={[
                              'h-4 w-4 mt-0.5 flex-shrink-0',
                              featured ? 'text-lime' : 'text-primary',
                            ].join(' ')}
                            strokeWidth={2.5}
                          />
                          <span>{t(`${baseKey}.${b}`)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA row */}
                  <footer className="relative z-10 flex items-center justify-between pt-6 border-t border-border/60">
                    <span className="font-mono-tag text-xs uppercase tracking-[0.24em] text-muted-foreground link-underline">
                      {t('services.seeAll')}
                    </span>
                    <span
                      className={[
                        'flex h-10 w-10 items-center justify-center rounded-full border transition-all duration-500',
                        featured
                          ? 'border-lime/40 text-lime group-hover:bg-lime group-hover:text-lime-foreground group-hover:border-lime'
                          : 'border-border text-foreground group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary',
                      ].join(' ')}
                      aria-hidden="true"
                    >
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </span>
                  </footer>
                </Link>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
